import { IonModalCustomEvent, OverlayEventDetail } from '@ionic/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { Form, Formik } from 'formik';
import { close } from 'ionicons/icons';
import { capitalize } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef } from 'react';
import * as yup from 'yup';
import {
  FindRolesOptionsBaseRolesFilter,
  ListRolesDocument,
  ListTeamsInput,
  ListTeamsQuery,
  Role,
  Team,
  TeamRole,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useListRolesQuery,
  useListTeamsQuery,
  useUpdateRoleMutation,
} from '../../api/graphql/generated';
import { useHasFeature } from '../../api/hooks/feature.hooks';
import {
  trackCustomRoleCreated,
  trackCustomRoleDeleted,
} from '../../config/analytics';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { logger } from '../../util/logger';
import { useConfirmActionAlert } from '../alert/confirmation-alert';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { LoadingButton } from '../button/loading-button';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormPicklistModalField } from '../form/ion-form-picklist-modal.field';
import {
  IonFormPicklistField,
  PicklistOption,
} from '../form/ion-form-picklist.field';
import { IonFormField } from '../form/ion-form.field';
import { SaveStatusToast } from '../toast/toast.gql';
import { RoleTypeToLocalizedKey } from './role.types';

const RoleValidationSchema = yup.object().shape({
  name: yup.string().required('*'),
  type: yup.string().oneOf(Object.values(TeamRole)).required('*'),
  teams: yup.array().of(yup.object().shape({ id: yup.string() })),
});

interface SaveRoleModalProps {
  isOpen: boolean;
  onDidDismiss?: (role?: Role) => void;
  onDelete?: () => void;
  role?: Role;
}

type RoleToSave = Omit<Role, 'id'> & { id: string | undefined };

const RoleDetailModal = (props: SaveRoleModalProps): ReactElement => {
  const { isOpen, onDidDismiss, onDelete, role } = props;

  const modalRef = useRef<HTMLIonModalElement>(null);

  const { data: listRolesData } = useListRolesQuery({
    variables: {
      input: {
        baseRolesFilter: FindRolesOptionsBaseRolesFilter.ONLY,
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  const baseRoles = listRolesData?.listRoles.items;

  const [deleteRole, { loading: deleteRoleLoading, error: deleteRoleError }] =
    useDeleteRoleMutation();
  const [updateRole, { loading: updateLoading, error: updateError }] =
    useUpdateRoleMutation();
  const [createRole, { loading: createLoading, error: createError }] =
    useCreateRoleMutation({
      awaitRefetchQueries: true,
      refetchQueries: [ListRolesDocument],
    });

  const isLoading = updateLoading || createLoading;

  const innerOnDidDismiss = useCallback(
    (e: IonModalCustomEvent<OverlayEventDetail<Role>>) => {
      if (typeof onDidDismiss === 'function') {
        onDidDismiss(e.detail.data);
      }
    },
    [onDidDismiss]
  );

  const hasAllRoles = useHasFeature<boolean>('beacon-enhanced-roles-all-roles');

  const roleTypeOptions: PicklistOption[] = useMemo(() => {
    const roles = hasAllRoles
      ? baseRoles
      : baseRoles?.filter((role) => role.type === TeamRole.OPERATOR);

    return (
      roles?.map<PicklistOption>((role) => {
        const labelKey = RoleTypeToLocalizedKey[role.type as TeamRole];

        return {
          label: getLocalizedText(labelKey) ?? capitalize(role.type),
          value: role.type,
        };
      }) ?? []
    );
  }, [baseRoles, hasAllRoles]);

  const initialValues: RoleToSave = useMemo(() => {
    return (
      role ??
      ({
        type: TeamRole.OPERATOR,
      } as RoleToSave)
    );
  }, [role]);

  const onSubmit = useCallback(
    async (role: RoleToSave) => {
      let savedRole: Role | undefined;
      try {
        if (role.id) {
          const result = await updateRole({
            variables: {
              input: {
                role: {
                  id: role.id,
                  name: role.name,
                  type: role.type as TeamRole,
                },
                teamIds: role.teams?.map((team) => team.id) ?? [],
              },
            },
          });

          savedRole = result.data?.updateRole;
        } else {
          const result = await createRole({
            variables: {
              input: {
                role: {
                  name: role.name,
                  type: role.type as TeamRole,
                },
                teamIds: role.teams?.map((team) => team.id) ?? [],
              },
            },
          });

          trackCustomRoleCreated({
            roleType: role.type,
          });
          savedRole = result.data?.createRole;
        }
      } catch (error) {
        logger.error('Failed to save role.', { error, role });
        return;
      }
      modalRef.current?.dismiss(savedRole);
    },
    [createRole, updateRole]
  );

  const submitLabel = useMemo(() => {
    return role
      ? getLocalizedText('common:update_role')
      : getLocalizedText('common:create_role');
  }, [role]);

  const roleName = getLocalizedText(
    RoleTypeToLocalizedKey[role?.type as TeamRole]
  );
  const confirmActionAlert = useConfirmActionAlert();
  const handleDelete = useMemo(() => {
    if (!role) {
      return;
    }

    return confirmActionAlert({
      confirmationAction: async () => {
        try {
          const deleted = await deleteRole({
            variables: {
              input: {
                id: role.id,
              },
            },
            awaitRefetchQueries: true,
            refetchQueries: [ListRolesDocument],
          });

          trackCustomRoleDeleted({
            roleType: role.type,
          });

          if (deleted.data?.deleteRole && typeof onDelete === 'function') {
            onDelete();
          }
        } catch (error) {
          logger.error('Failed to delete role.', {
            error,
            role,
          });
          return;
        }
      },
      header: getLocalizedText('common:delete_role'),
      message:
        getLocalizedText('common:delete_role_confirmation_prompt', {
          role: roleName,
        }) ??
        `Users assigned this role will be reassigned to the "${roleName}" role.`,
      confirmText: getLocalizedText('common:delete'),
      isDestructive: true,
    });
  }, [confirmActionAlert, deleteRole, onDelete, role, roleName]);

  return (
    <IonModal ref={modalRef} isOpen={isOpen} onDidDismiss={innerOnDidDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              onClick={() => {
                modalRef.current?.dismiss();
              }}
            >
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>{getLocalizedText('common:role_details')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={RoleValidationSchema}
        onSubmit={onSubmit}
      >
        {({ submitForm, isValid, dirty }) => {
          const submitDisabled = !isValid || !dirty || deleteRoleLoading;
          return (
            <>
              <IonContent>
                <Form>
                  <IonList lines="none">
                    <IonFormField
                      label={getLocalizedText('common:name') ?? 'Name'}
                      name="name"
                      inputProps={{
                        type: 'text',
                        autocapitalize: 'on',
                      }}
                    />
                    <IonFormPicklistField
                      label={getLocalizedText('common:type') ?? 'Type'}
                      name="type"
                      options={roleTypeOptions}
                      selectProps={{
                        disabled: true,
                      }}
                    />
                    <IonFormPicklistModalField<
                      Team,
                      ListTeamsQuery,
                      ListTeamsInput
                    >
                      name="teams"
                      label={getLocalizedText('common:teams') ?? 'Teams'}
                      useQuery={useListTeamsQuery}
                      getObjectsFromListQuery={(data) => {
                        return data?.listTeams.items;
                      }}
                      isEmptyAll={true}
                      getObjectDisplayName={(team) => team.name}
                      inputProps={{
                        inputPlaceholder:
                          getLocalizedText('common:click_to_select_teams') ??
                          'Click to select teams',
                      }}
                      modalProps={{
                        modalHeader:
                          getLocalizedText('common:assign_teams') ??
                          'Assign Teams',
                        selectedObjectsHeader:
                          getLocalizedText('common:assigned') ?? 'Assigned',
                        availableObjectsHeader:
                          getLocalizedText('common:unassigned') ?? 'Unassigned',
                        deselectConfirmationHeader:
                          getLocalizedText(
                            'common:role_team_unassign_confirmation'
                          ) ?? 'Are you sure you want to unassign this team?',
                        deselectConfirmationButtonText:
                          getLocalizedText('common:unassign') ?? 'Unassign',
                        noSelectedObjectsMessage:
                          getLocalizedText('common:all_teams_assigned') ??
                          'All teams are assigned',
                        noAvailableObjectsMessage:
                          getLocalizedText('common:no_teams_to_assign') ??
                          'No teams to assign',
                        searchbarPlaceholder:
                          getLocalizedText('common:search_for_team') ??
                          'Search for team',
                      }}
                    />
                  </IonList>
                </Form>
              </IonContent>
              <IonFooter>
                <IonToolbar>
                  <RightAlignedButtons>
                    {role && (
                      <LoadingButton
                        loading={deleteRoleLoading}
                        buttonProps={{
                          type: 'submit',
                          color: 'danger',
                          disabled: false,
                          fill: 'outline',
                          onClick: handleDelete,
                        }}
                      >
                        {getLocalizedText('common:delete_role')}
                      </LoadingButton>
                    )}
                    <FormSubmitButton
                      label={submitLabel}
                      loading={isLoading}
                      buttonProps={{
                        type: 'submit',
                        onClick: submitForm,
                      }}
                      disabled={submitDisabled}
                    />
                  </RightAlignedButtons>
                </IonToolbar>
              </IonFooter>
            </>
          );
        }}
      </Formik>
      <SaveStatusToast
        error={createError}
        defaultErrorMessageKey="common:create_role_error"
        loading={createLoading}
        showOnly="error"
      />
      <SaveStatusToast
        defaultErrorMessageKey="common:update_role_error"
        error={updateError}
        loading={updateLoading}
      />
      <SaveStatusToast
        defaultErrorMessageKey="common:delete_role_error"
        error={deleteRoleError}
        loading={deleteRoleLoading}
        showOnly="error"
      />
    </IonModal>
  );
};

export { RoleDetailModal };
